import { useState } from 'react';
import type { OnboardingBasicsInput } from '@st/contract';
import { useSubmitBasics } from '../../api/queries';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Chip, Input, NumberInput } from '../../ui';
import { WizardScreen } from './WizardScreen';

/** Redoslijed ekrana prvog koraka — jedno polje po ekranu, kako je trener tražio. */
export const BASICS_FIELDS = [
  'firstName',
  'lastName',
  'birthDate',
  'gender',
  'heightCm',
  'weightKg',
] as const;

export type BasicsField = (typeof BASICS_FIELDS)[number];

type Gender = OnboardingBasicsInput['gender'];

const GENDERS: readonly Gender[] = ['female', 'male', 'other', 'undisclosed'];

const GENDER_KEY: Record<Gender, string> = {
  female: 'wizard.genderFemale',
  male: 'wizard.genderMale',
  other: 'wizard.genderOther',
  undisclosed: 'wizard.genderUndisclosed',
};

export interface BasicsValues {
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: Gender | '';
  heightCm: number | '';
  weightKg: number | '';
}

type Values = BasicsValues;

const EMPTY: Values = {
  firstName: '',
  lastName: '',
  birthDate: '',
  gender: '',
  heightCm: '',
  weightKg: '',
};

/** Bez ispravne vrijednosti se ne ide dalje — korak se ne može preskočiti. */
export function basicsFieldError(field: BasicsField, values: Values): string | null {
  switch (field) {
    case 'firstName':
      return values.firstName.trim() ? null : 'wizard.required';
    case 'lastName':
      return values.lastName.trim() ? null : 'wizard.required';
    case 'birthDate':
      return /^\d{4}-\d{2}-\d{2}$/.test(values.birthDate) ? null : 'wizard.invalidDate';
    case 'gender':
      return values.gender ? null : 'wizard.required';
    case 'heightCm':
      return typeof values.heightCm === 'number' &&
        values.heightCm >= 100 &&
        values.heightCm <= 250
        ? null
        : 'wizard.invalidHeight';
    case 'weightKg':
      return typeof values.weightKg === 'number' && values.weightKg >= 30 && values.weightKg <= 300
        ? null
        : 'wizard.invalidWeight';
  }
}

function isFieldEmpty(field: BasicsField, values: Values): boolean {
  const value = values[field];
  return value === '' || value === undefined;
}

export interface BasicsStepProps {
  /** Indeks ekrana unutar koraka; traku napretka drži čarobnjak. */
  fieldIndex: number;
  onFieldIndexChange: (next: number) => void;
  screenIndex: number;
  totalScreens: number;
  onDone: () => void;
  onBackToStart?: (() => void) | undefined;
}

/**
 * Prvi korak: ime → prezime → datum rođenja → spol → visina → težina.
 * Na kraju ide JEDAN poziv `onboarding.basics` — server tada upisuje i prvu mjeru
 * težine, pa graf ne kreće od praznog.
 */
export function BasicsStep({
  fieldIndex,
  onFieldIndexChange,
  screenIndex,
  totalScreens,
  onDone,
  onBackToStart,
}: BasicsStepProps) {
  const { t } = useTranslation();
  const submit = useSubmitBasics();
  const [values, setValues] = useState<Values>(EMPTY);

  const field = BASICS_FIELDS[fieldIndex] ?? BASICS_FIELDS[0]!;
  const errorKey = basicsFieldError(field, values);
  const isLast = fieldIndex === BASICS_FIELDS.length - 1;

  function set<K extends keyof Values>(key: K, value: Values[K]): void {
    setValues((v) => ({ ...v, [key]: value }));
  }

  async function next(): Promise<void> {
    if (errorKey) return;
    if (!isLast) {
      onFieldIndexChange(fieldIndex + 1);
      return;
    }
    await submit.mutateAsync({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      birthDate: values.birthDate,
      gender: values.gender as Gender,
      heightCm: Number(values.heightCm),
      weightKg: Number(values.weightKg),
    });
    onDone();
  }

  const serverError = submit.isError ? toErrorMessage(t, submit.error).title : undefined;
  // Prazno polje samo drži „Dalje” zaključanim; poruka se pojavi tek kad je unos kriv.
  const fieldError = errorKey && !isFieldEmpty(field, values) ? t(errorKey) : undefined;

  return (
    <WizardScreen
      eyebrow={t('wizard.basics')}
      question={t(`wizard.${field}`)}
      {...(field === 'birthDate' ? { help: t('wizard.birthDateHint') } : {})}
      {...(field === 'weightKg' ? { help: t('wizard.weightHint') } : {})}
      index={screenIndex}
      total={totalScreens}
      onNext={() => void next()}
      onBack={fieldIndex === 0 ? onBackToStart : () => onFieldIndexChange(fieldIndex - 1)}
      nextDisabled={Boolean(errorKey)}
      loading={submit.isPending}
      error={fieldError ?? serverError}
    >
      {field === 'firstName' && (
        <Input
          id="wz-firstName"
          label={t('wizard.firstNameLabel')}
          autoFocus
          value={values.firstName}
          onChange={(e) => set('firstName', e.target.value)}
        />
      )}
      {field === 'lastName' && (
        <Input
          id="wz-lastName"
          label={t('wizard.lastNameLabel')}
          autoFocus
          value={values.lastName}
          onChange={(e) => set('lastName', e.target.value)}
        />
      )}
      {field === 'birthDate' && (
        <Input
          id="wz-birthDate"
          type="date"
          label={t('wizard.birthDateLabel')}
          value={values.birthDate}
          onChange={(e) => set('birthDate', e.target.value)}
        />
      )}
      {field === 'gender' && (
        <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={t('wizard.genderLabel')}>
          {GENDERS.map((g) => (
            <Chip key={g} selected={values.gender === g} onClick={() => set('gender', g)}>
              {t(GENDER_KEY[g])}
            </Chip>
          ))}
        </div>
      )}
      {field === 'heightCm' && (
        <NumberInput
          id="wz-heightCm"
          label={t('wizard.heightLabel')}
          suffix="cm"
          autoFocus
          value={values.heightCm}
          onValueChange={(v) => set('heightCm', v)}
        />
      )}
      {field === 'weightKg' && (
        <NumberInput
          id="wz-weightKg"
          label={t('wizard.weightLabel')}
          suffix="kg"
          step={0.1}
          autoFocus
          value={values.weightKg}
          onValueChange={(v) => set('weightKg', v)}
        />
      )}
    </WizardScreen>
  );
}
